import './i18n/autoTranslate';

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import SpotlightCard from '../components/ui/SpotlightCard';
import AppBackground from '../components/ui/AppBackground.tsx';

import { Link, useNavigate } from 'react-router-dom';
import TextType from '../components/ui/TextType';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../features/auth/authThunks';
import { setUser, setToken } from '../features/auth/authSlice';
import '../components/styles/Loading.css';
import { FaLinkedin } from "react-icons/fa6";
import { motion } from 'framer-motion';
import LoadingSpinner from '../../src/LoadingSpinner';
import SEO from './SEO';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export function LoginPage() {
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [loaderState, setLoaderState] = useState(false);

  // Listen for OAuth popup messages
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.origin !== API_URL) return;

      const { type, provider, token, refreshToken, user, error } = event.data;

      if (type === 'oauth_success') {
        console.log(`${provider} OAuth successful`);

        localStorage.setItem('access_token', token);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem('user', JSON.stringify(user));

        dispatch(setToken(token));
        dispatch(setUser(user));

        setLoaderState(false);

        setTimeout(() => {
          navigate('/dashboard-home');
        }, 1000);
      } else if (type === 'oauth_error') {
        console.error(`${provider} OAuth error:`, error);
        setLoaderState(false);
        setFormErrors({ submit: `${provider} authentication failed: ${error}` });
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [dispatch, navigate, API_URL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoaderState(true);

    const credentials = {
      email: formData.email,
      password: formData.password
    };

    try {
      const result = await dispatch(loginUser(credentials)).unwrap();
      console.log('Login successful:', result);
      localStorage.setItem('access_token', result.token);
      localStorage.setItem("refreshToken", result.refreshToken);
      localStorage.setItem('user', JSON.stringify(result.user));
      if (result.success) {
        setTimeout(() => {
          setLoaderState(false);
          navigate('/dashboard-home');
        }, 2000);
      } else {
        alert("Login failed: " + result.message);
      }
    } catch (err) {
      console.error('Login failed:', err);
      setLoaderState(false);
      setFormErrors({ submit: err.message || 'Login failed' });
    }
  };

  // OAuth handlers with popup
  const handleGoogleLogin = () => {
    setLoaderState(true);
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      `${API_URL}/api/auth/google/login`,
      'Google Login',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const handleLinkedInLogin = () => {
    setLoaderState(true);
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      `${API_URL}/api/auth/linkedin/login`,
      'LinkedIn Login',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const glassCard =
    "relative rounded-[28px] border border-white/10 bg-white/[0.04] backdrop-blur-2xl " +
    "shadow-[0_22px_70px_-40px_rgba(0,0,0,0.85)] overflow-hidden";

  const panelBase =
    "relative p-7 sm:p-10 lg:p-12";

  const inputBase =
    "w-full px-4 py-3.5 rounded-2xl backdrop-blur-sm " +
    "bg-white/[0.04] border border-white/10 text-white placeholder-white/40 " +
    "focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/35 focus:border-white/15 " +
    "transition-all duration-300";

  const subtleGrid =
    "absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]";

  return (
    <AppBackground>
      {/* Enhanced Loading Overlay */}
      {loaderState && (
        // <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[100000000]">
        //   <div className="loader-container flex flex-col items-center justify-center">
        //     <div className="relative">
        //       <div className="w-20 h-20 rounded-full border-4 border-transparent border-t-[#6C63FF] border-r-[#6C63FF] animate-spin"></div>
        //       <div className="absolute inset-0 flex items-center justify-center">
        //         <Zap className="w-8 h-8 text-[#6C63FF] animate-pulse" />
        //       </div>
        //     </div>
        //     <p className="text-white mt-6 text-lg font-medium bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
        //       Signing you in...
        //     </p>
        //     <p className="text-white/60 mt-2 text-sm">Preparing your interview workspace</p>
        //   </div>
        // </div>
        <LoadingSpinner />
      )}
<SEO
  title="Login — InterviewGenieAI"
  description="Log in to InterviewGenieAI to continue practicing AI interviews, view feedback, track your progress, and improve your interview performance."
  canonical="https://interviewgenieai.com/login"
/>
      {/* Background (TechApp-ish) */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-28 -left-28 w-[560px] h-[560px] rounded-full bg-[#6C63FF]/12 blur-[90px]" />
        <div className="absolute -bottom-28 -right-28 w-[560px] h-[560px] rounded-full bg-[#39B6FF]/10 blur-[95px]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_700px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
        <div className={subtleGrid} />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/70" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 pt-24 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className={`max-w-6xl w-full ${loaderState ? 'blur-sm' : ''}`}
        >
          <SpotlightCard className={glassCard}>
            {/* Card chrome */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#6C63FF]/10 blur-[110px]" />
            <div className="absolute -bottom-24 left-1/3 w-[520px] h-[520px] rounded-full bg-[#39B6FF]/8 blur-[110px]" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Panel - Login Form */}
              <div className={`${panelBase} border-b lg:border-b-0 lg:border-r border-white/10`}>
                {/* Top micro-bar */}
                <div className="flex items-center justify-between mb-10">
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-white/85" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold tracking-tight text-white">InterviewGenieAI</div>
                      <div className="text-[11px] text-white/55 -mt-0.5">Secure sign-in</div>
                    </div>
                  </div>

                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70">
                    <Shield size={14} className="text-[#6C63FF]" />
                    <span>Encrypted</span>
                  </div>
                </div>

                <div className="max-w-md mx-auto">
                  {/* Header */}
                  <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 mb-5">
                      <span className="h-px w-10 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                      <span className="text-[12px] font-semibold tracking-[0.22em] text-[#6C63FF] uppercase">
                        Welcome Back
                      </span>
                      <span className="h-px w-10 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                    </div>

                    <h1 className="pb-2 text-[34px] sm:text-4xl font-semibold tracking-tight bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                      Sign in
                    </h1>
                    <p className="text-white/60 mt-2">
                      Continue your journey to interview excellence
                    </p>
                  </div>

                  {/* OAuth Buttons */}
                  <div className="mb-8">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={handleGoogleLogin}
                        className="relative group p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#6C63FF]/0 via-white/0 to-[#39B6FF]/0 group-hover:from-[#6C63FF]/8 group-hover:via-white/3 group-hover:to-[#39B6FF]/8 transition-all duration-500 rounded-2xl" />
                        <svg className="w-5 h-5 relative z-10" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span className="text-sm font-semibold text-white/90 relative z-10">Google</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleLinkedInLogin}
                        className="relative group p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A66C2]/0 via-white/0 to-[#6C63FF]/0 group-hover:from-[#0A66C2]/10 group-hover:via-white/3 group-hover:to-[#6C63FF]/8 transition-all duration-500 rounded-2xl" />
                        <FaLinkedin className="w-5 h-5 relative z-10 text-[#0A66C2]" />
                        <span className="text-sm font-semibold text-white/90 relative z-10">LinkedIn</span>
                      </button>
                    </div>

                    <div className="flex items-center my-7">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                      <span className="px-4 text-xs tracking-wide text-white/45 uppercase">or email</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                    </div>
                  </div>

                  {/* Error Message */}
                  {formErrors.submit && (
                    <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <p className="text-sm text-red-200">{formErrors.submit}</p>
                      </div>
                    </div>
                  )}

                  {/* Login Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-white/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Mail size={16} className="text-[#6C63FF]" />
                          <span>Email Address</span>
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`${inputBase} ${formErrors.email ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                          placeholder="you@example.com"
                        />
                        {formErrors.email && (
                          <p className="absolute -bottom-5 left-1 text-xs text-red-300">{formErrors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Lock size={16} className="text-[#6C63FF]" />
                          <span>Password</span>
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          className={`${inputBase} pr-12 ${formErrors.password ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        {formErrors.password && (
                          <p className="absolute -bottom-5 left-1 text-xs text-red-300">{formErrors.password}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <div className="relative">
                          <input
                            type="checkbox"
                            name="rememberMe"
                            checked={formData.rememberMe}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div
                            className={[
                              "w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-300",
                              formData.rememberMe
                                ? "bg-[#6C63FF] border-[#6C63FF] shadow-[0_0_0_4px_rgba(108,99,255,0.18)]"
                                : "bg-white/5 border-white/20 hover:border-white/30",
                            ].join(" ")}
                          >
                            {formData.rememberMe && <div className="w-2 h-2 rounded-sm bg-white" />}
                          </div>
                        </div>
                        <span className="text-sm text-white/75">Remember me</span>
                      </label>

                      <Link
                        to="/forgot-password"
                        className="text-sm text-[#6C63FF] hover:text-[#8B7CFF] transition-colors"
                      >
                        Forgot password?
                      </Link>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading || loaderState}
                      style={{
                        boxShadow:
                          'inset 0 -4px 10px rgba(255, 255, 255, 0.22), 0 0 55px rgba(108, 99, 255, 0.18)'
                      }}
                      className="px-8 py-3.5 w-full group rounded-2xl text-white font-semibold transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden
                                 bg-gradient-to-br from-[#8B7CFF] via-[#6C63FF] to-[#39B6FF]
                                 hover:shadow-[0_0_70px_rgba(108,99,255,0.25)]"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(800px_260px_at_50%_120%,rgba(255,255,255,0.35),transparent_60%)]" />
                      <div className="absolute -bottom-1 left-[18%] w-[64%] h-4 rounded-full bg-white/60 blur-[12px] pointer-events-none" />

                      {loading || loaderState ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Signing In...</span>
                        </>
                      ) : (
                        <>
                          <span>Sign In</span>
                          <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                        </>
                      )}
                    </motion.button>
                  </form>

                  {/* Sign Up Link */}
                  <div className="mt-8 pt-8 border-t border-white/10 text-center">
                    <p className="text-white/60 text-sm">
                      Don't have an account?{' '}
                      <Link
                        to="/sign-up"
                        className="text-[#6C63FF] hover:text-[#8B7CFF] font-semibold transition-colors group inline-flex items-center gap-1"
                      >
                        Sign up now
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Panel - Visual Showcase */}
              <div className={`${panelBase} relative`}>
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6C63FF]/8 via-transparent to-[#39B6FF]/8" />
                  <div className="absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-[#6C63FF]/10 blur-[90px]" />
                  <div className="absolute -bottom-28 -left-28 h-[520px] w-[520px] rounded-full bg-[#39B6FF]/8 blur-[95px]" />
                  <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:30px_30px]" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/25" />
                </div>

                <div className="relative h-full flex flex-col">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/6 border border-white/10 w-fit">
                    <Sparkles size={16} className="text-[#6C63FF]" />
                    <span className="text-sm font-semibold text-white/80">AI-Powered Excellence</span>
                  </div>

                  <div className="mt-8">
                    <TextType
                      text={[
                        "Transform interview anxiety into confidence.",
                        "Master every interview with AI-powered insights.",
                        "From practice to performance — excellence redefined."
                      ]}
                      typingSpeed={60}
                      pauseDuration={2000}
                      showCursor={true}
                      cursorCharacter="_"
                      className="text-3xl sm:text-4xl font-semibold tracking-tight bg-gradient-to-r from-white via-white/85 to-white bg-clip-text text-transparent leading-tight"
                    />
                    <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
                      Structured practice. Real-time feedback. Clean analytics. Built to feel like a serious hiring loop.
                    </p>
                  </div>

                  <div className="mt-10 grid grid-cols-2 gap-4">
                    {[
                      { label: "Real-time Feedback", accent: "from-[#39B6FF]/70 to-cyan-500/70" },
                      { label: "AI Analysis", accent: "from-[#6C63FF]/70 to-pink-500/70" },
                      { label: "Progress Tracking", accent: "from-emerald-500/70 to-green-400/70" },
                      { label: "Confidence Score", accent: "from-orange-500/70 to-red-500/70" }
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-xl
                                   hover:bg-white/[0.06] hover:border-white/15 transition-all duration-300"
                      >
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${item.accent} mb-3`} />
                        <p className="text-sm font-semibold text-white/85">{item.label}</p>
                        <p className="text-xs text-white/55 mt-1">High-signal coaching, not noise.</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-10">
                    <div className="flex items-center justify-center gap-4 text-sm text-white/55">
                      <div className="flex items-center gap-2">
                        <Shield size={14} />
                        <span>Secure & Encrypted</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-white/20" />
                      <div className="flex items-center gap-2">
                        <Zap size={14} />
                        <span>Fast & Reliable</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </motion.div>
      </div>
    </AppBackground>
  );
}
